import {
  Box,
  Stack,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Paper,
  alpha,
} from '@mui/material';

import { AlertCircleIcon } from '@/assets/icons/AlertCircleIcon';
import { CalendarIcon } from '@/assets/icons/CalendarIcon';
import { Home03Icon } from '@/assets/icons/Home03Icon';
import { Lightbulb01Icon } from '@/assets/icons/Lightbulb01Icon';
import { MessageCircle01Icon } from '@/assets/icons/MessageCircle01Icon';
import { PiggyBank02Icon } from '@/assets/icons/PiggyBank02Icon';
import { PlusIcon } from '@/assets/icons/PlusIcon';
import { Shield01Icon } from '@/assets/icons/Shield01Icon';
import { Star01Icon } from '@/assets/icons/Star01Icon';
import { Tool01Icon } from '@/assets/icons/Tool01Icon';
import { Users02Icon } from '@/assets/icons/Users02Icon';

interface CommunityHubFirstTimeExperienceProps {
  onCreatePost: () => void;
  canCreatePost: boolean;
  isUserMember: boolean;
  onJoinCommunity?: () => void;
}

interface DiscussionExample {
  icon: React.ReactNode;
  title: string;
  description: string;
  category: string;
}

const discussionExamples: DiscussionExample[] = [
  {
    icon: <Tool01Icon width={24} height={24} />,
    title: 'Maintenance Tips',
    description: 'Share DIY fixes and get advice on home repairs',
    category: 'Maintenance',
  },
  {
    icon: <AlertCircleIcon width={24} height={24} />,
    title: 'Safety Alerts',
    description: 'Keep neighbors informed about security concerns',
    category: 'Safety',
  },
  {
    icon: <CalendarIcon width={24} height={24} />,
    title: 'Community Events',
    description: 'Organize gatherings and neighborhood activities',
    category: 'Events',
  },
  {
    icon: <Lightbulb01Icon width={24} height={24} />,
    title: 'Local Recommendations',
    description: 'Find trusted contractors and service providers',
    category: 'Resources',
  },
  {
    icon: <Home03Icon width={24} height={24} />,
    title: 'Property Updates',
    description: 'Share renovations and improvement projects',
    category: 'Updates',
  },
  {
    icon: <Shield01Icon width={24} height={24} />,
    title: 'Rules & Guidelines',
    description: 'Discuss community policies and regulations',
    category: 'Governance',
  },
];

const CommunityHubFirstTimeExperience: React.FC<CommunityHubFirstTimeExperienceProps> = ({
  onCreatePost,
  canCreatePost,
  isUserMember,
  onJoinCommunity,
}) => {
  const getCategoryColor = (category: string) => {
    switch (category) {
    case 'Maintenance':
      return '#ff9800';
    case 'Safety':
      return '#f44336';
    case 'Events':
      return '#2196f3';
    case 'Resources':
      return '#4caf50';
    case 'Updates':
      return '#9c27b0';
    case 'Governance':
      return '#607d8b';
    default:
      return '#9e9e9e';
    }
  };

  return (
    <Stack spacing={4} sx={{ py: 2 }}>
      {/* Hero Section */}
      <Box sx={{ textAlign: 'center' }}>
        <Stack
          direction="row"
          spacing={1}
          justifyContent="center"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Box sx={{ color: 'primary.main' }}>
            <MessageCircle01Icon width={48} height={48} />
          </Box>
          <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main' }}>
            Community Hub
          </Typography>
        </Stack>
        <Typography variant="h5" sx={{ fontWeight: 600, color: 'secondary.900', mb: 2 }}>
          Connect, share, and collaborate with your neighbors
        </Typography>
        <Typography variant="body1" sx={{ color: 'secondary.700', maxWidth: 600, mx: 'auto' }}>
          The Community Hub is your central space for discussions, announcements, and building
          stronger connections with fellow residents.
        </Typography>
      </Box>

      {/* What You Can Do */}
      <Card
        elevation={0}
        sx={{
          bgcolor: 'secondary.50',
          border: '1px solid',
          borderColor: 'secondary.200',
          borderRadius: 2,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: 'secondary.900', mb: 3, textAlign: 'center' }}
          >
            What You Can Do Here
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Stack spacing={1.5} alignItems="center" textAlign="center">
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    bgcolor: 'primary.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'primary.main',
                  }}
                >
                  <MessageCircle01Icon width={28} height={28} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Start Discussions
                </Typography>
                <Typography variant="body2" sx={{ color: 'secondary.600' }}>
                  Ask questions, share ideas, and get feedback from neighbors
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} md={3}>
              <Stack spacing={1.5} alignItems="center" textAlign="center">
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    bgcolor: 'primary.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'primary.main',
                  }}
                >
                  <AlertCircleIcon width={28} height={28} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Share Updates
                </Typography>
                <Typography variant="body2" sx={{ color: 'secondary.600' }}>
                  Keep everyone informed about important community matters
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} md={3}>
              <Stack spacing={1.5} alignItems="center" textAlign="center">
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    bgcolor: 'primary.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'primary.main',
                  }}
                >
                  <Users02Icon width={28} height={28} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Build Connections
                </Typography>
                <Typography variant="body2" sx={{ color: 'secondary.600' }}>
                  Get to know your neighbors and strengthen community bonds
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} md={3}>
              <Stack spacing={1.5} alignItems="center" textAlign="center">
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    bgcolor: 'primary.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'primary.main',
                  }}
                >
                  <Star01Icon width={28} height={28} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Find Resources
                </Typography>
                <Typography variant="body2" sx={{ color: 'secondary.600' }}>
                  Discover local services, tips, and community knowledge
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Popular Discussion Topics */}
      <Box>
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: 'secondary.900', mb: 3, textAlign: 'center' }}
        >
          Popular Discussion Topics
        </Typography>
        <Grid container spacing={2}>
          {discussionExamples.map((example) => (
            <Grid item xs={12} sm={6} md={4} key={example.title}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 0.2s ease-in-out',
                  height: '100%',
                  '&:hover': {
                    borderColor: 'primary.main',
                    transform: 'translateY(-2px)',
                    boxShadow: 2,
                  },
                }}
              >
                <Stack spacing={2}>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 1.5,
                        bgcolor: alpha(getCategoryColor(example.category), 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: getCategoryColor(example.category),
                      }}
                    >
                      {example.icon}
                    </Box>
                    <Chip
                      label={example.category}
                      size="small"
                      sx={{
                        bgcolor: alpha(getCategoryColor(example.category), 0.1),
                        color: getCategoryColor(example.category),
                        fontWeight: 500,
                        fontSize: '0.75rem',
                      }}
                    />
                  </Stack>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: 'secondary.900', mb: 0.5 }}
                    >
                      {example.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'secondary.600', lineHeight: 1.5 }}>
                      {example.description}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* How It Works */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'primary.200',
          bgcolor: 'primary.50',
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: 'secondary.900', mb: 3, textAlign: 'center' }}
        >
          How the Community Hub Works
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <Stack spacing={1.5} textAlign="center">
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  bgcolor: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'primary.main',
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  mx: 'auto',
                  boxShadow: '0 2px 8px rgba(157, 21, 0, 0.1)',
                }}
              >
                1
              </Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Create a Post
              </Typography>
              <Typography variant="body2" sx={{ color: 'secondary.600' }}>
                Share your thoughts, questions, or announcements with the community
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Stack spacing={1.5} textAlign="center">
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  bgcolor: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'primary.main',
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  mx: 'auto',
                  boxShadow: '0 2px 8px rgba(157, 21, 0, 0.1)',
                }}
              >
                2
              </Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Engage & Discuss
              </Typography>
              <Typography variant="body2" sx={{ color: 'secondary.600' }}>
                Reply to posts, like content, and participate in conversations
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Stack spacing={1.5} textAlign="center">
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  bgcolor: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'primary.main',
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  mx: 'auto',
                  boxShadow: '0 2px 8px rgba(157, 21, 0, 0.1)',
                }}
              >
                3
              </Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Stay Connected
              </Typography>
              <Typography variant="body2" sx={{ color: 'secondary.600' }}>
                Build relationships and stay informed about your community
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Community Features */}
      <Stack direction="row" spacing={3} sx={{ justifyContent: 'center', flexWrap: 'wrap' }}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'secondary.200',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            minWidth: { xs: '100%', sm: 'auto' },
          }}
        >
          <Box sx={{ color: 'success.main' }}>
            <PiggyBank02Icon width={32} height={32} />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Save Together Groups
            </Typography>
            <Typography variant="body2" sx={{ color: 'secondary.600' }}>
              Join forces for group discounts
            </Typography>
          </Box>
        </Paper>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'secondary.200',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            minWidth: { xs: '100%', sm: 'auto' },
          }}
        >
          <Box sx={{ color: 'info.main' }}>
            <CalendarIcon width={32} height={32} />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Community Calendar
            </Typography>
            <Typography variant="body2" sx={{ color: 'secondary.600' }}>
              Track events and activities
            </Typography>
          </Box>
        </Paper>
      </Stack>

      {/* CTA Section */}
      <Box sx={{ textAlign: 'center', py: 2 }}>
        {isUserMember ? (
          <>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'secondary.900', mb: 2 }}>
              Ready to connect with your community?
            </Typography>
            <Button
              startIcon={<PlusIcon />}
              variant="contained"
              size="large"
              onClick={onCreatePost}
              disabled={!canCreatePost}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                fontWeight: 600,
                px: 4,
                py: 1.75,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                boxShadow: '0px 4px 12px rgba(157, 21, 0, 0.25)',
                '&:hover': {
                  bgcolor: 'primary.dark',
                  boxShadow: '0px 6px 16px rgba(157, 21, 0, 0.35)',
                },
                '&.Mui-disabled': {
                  bgcolor: 'grey.300',
                  color: 'grey.500',
                },
              }}
            >
              Create Your First Post
            </Button>
            <Typography variant="body2" sx={{ color: 'secondary.600', mt: 2 }}>
              Start a discussion and help build a vibrant community
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'secondary.900', mb: 2 }}>
              Join the community to start participating
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={onJoinCommunity}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                fontWeight: 600,
                px: 4,
                py: 1.75,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                boxShadow: '0px 4px 12px rgba(157, 21, 0, 0.25)',
                '&:hover': {
                  bgcolor: 'primary.dark',
                  boxShadow: '0px 6px 16px rgba(157, 21, 0, 0.35)',
                },
              }}
            >
              Join Community
            </Button>
            <Typography variant="body2" sx={{ color: 'secondary.600', mt: 2 }}>
              Connect with your neighbors and access all community features
            </Typography>
          </>
        )}
      </Box>
    </Stack>
  );
};

export default CommunityHubFirstTimeExperience;